import bbox from '@turf/bbox'
import type { MultiPolygon } from 'geojson'
import { useEffect, useMemo, useRef, useState } from 'react'
import { aClient, useAtomValue } from '~/atoms'
import { useDebounce } from '~/hooks/misc'

const searchPlace = async (
  input: string,
  locationRestriction?: google.maps.LatLngBounds
): Promise<google.maps.places.AutocompletePrediction[] | null> => {
  return new Promise((resolve, reject) => {
    const service = new google.maps.places.AutocompleteService()
    service.getPlacePredictions(
      {
        input,
        types: ['geocode'],
        locationRestriction,
      },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(predictions)
        } else if (
          status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          resolve([])
        } else {
          reject(null)
        }
      }
    )
  })
}

const getPlace = (
  placeId: string
): Promise<google.maps.GeocoderResult | null> => {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ placeId }, (results, status) => {
      if (status == 'OK') {
        resolve(results![0])
      } else {
        reject(null)
      }
    })
  })
}

const getAddressValues = (result: google.maps.GeocoderResult) => {
  const addressParts = [
    result.address_components.find((comp) =>
      comp.types.includes('street_number')
    ),
    result.address_components.find((comp) =>
      comp.types.includes('intersection')
    ),
    result.address_components.find((comp) => comp.types.includes('route')),
  ]
    .filter((p) => p)
    .map((p) => p!.short_name)
  const city = result.address_components.find(
    (comp) =>
      comp.types.includes('locality') ||
      comp.types.includes('administrative_area_level_3')
  )
  const state = result.address_components.find((comp) =>
    comp.types.includes('administrative_area_level_1')
  )
  const postal = result.address_components.find((comp) =>
    comp.types.includes('postal_code')
  )
  const county = result.address_components.find((comp) =>
    comp.types.includes('administrative_area_level_2')
  )
  const values: {
    // [key: string]: string
    address: string
    city: string
    stateAbbr: string
    postalCode: string
    county: string
  } = {
    address: addressParts.length > 0 ? addressParts.join(' ') : '',
    city: city?.long_name || '',
    stateAbbr: state?.short_name || '',
    postalCode: postal?.long_name || '',
    county: county?.long_name || '',
  }
  return values
}

export function useGeocodeSearch() {
  const client = useAtomValue(aClient)
  const [search, setSearch] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const debouncedSearch = useDebounce(search.trim(), 200)
  const latestSearchRef = useRef(search.trim())

  const maxBounds = useMemo(() => {
    if (!client?.geom) {
      return
    }
    return bbox(client.geom as MultiPolygon)
  }, [client])

  const getPlaceData = async (
    prediction: google.maps.places.AutocompletePrediction
  ) => {
    const place = await getPlace(prediction.place_id)
    if (!place) {
      return null
    }
    const zoom = place!.types.includes('political') ? 9 : 14
    const lng = place!.geometry.location.lng()
    const lat = place!.geometry.location.lat()
    return {
      zoom,
      lng,
      lat,
      ...getAddressValues(place),
    }
  }

  useEffect(() => {
    latestSearchRef.current = search.trim()

    if (search.trim().length === 0) {
      setPredictions([])
      setIsLoading(false)
    }
  }, [search])

  useEffect(() => {
    if (debouncedSearch.length === 0) {
      return
    }

    let isCancelled = false
    setIsLoading(true)

    const loadPredictions = async () => {
      try {
        const bounds = !maxBounds
          ? undefined
          : new google.maps.LatLngBounds(
              new google.maps.LatLng(maxBounds[1], maxBounds[0]),
              new google.maps.LatLng(maxBounds[3], maxBounds[2])
            )

        const nextPredictions = await searchPlace(debouncedSearch, bounds)
        if (!isCancelled && latestSearchRef.current === debouncedSearch) {
          setPredictions(nextPredictions || [])
        }
      } catch (_) {
        if (!isCancelled && latestSearchRef.current === debouncedSearch) {
          setPredictions([])
        }
      } finally {
        if (!isCancelled && latestSearchRef.current === debouncedSearch) {
          setIsLoading(false)
        }
      }
    }

    void loadPredictions()

    return () => {
      isCancelled = true
    }
  }, [debouncedSearch, maxBounds])

  return {
    search,
    setSearch,
    isLoading,
    predictions,
    getPlaceData,
  }
}
